import { useState } from "react";
import { api, apiErrorMessage, setLocalSession } from "../api/client";
import { theme, card, btnPrimary, btnSecondary, inputStyle } from "../theme";
import { PasswordRules, passwordLooksAcceptable } from "./PasswordRules";
import { TotpEnrolment } from "./TotpEnrolment";

// Profile page "Password & MFA" card. SSO accounts get the pointer to their
// identity provider; password accounts can change the password, replace the
// authenticator app (new phone) and issue fresh recovery codes — each of which
// re-proves the password, and the MFA actions a current code too.

interface SecurityUser { hasLocalPassword: boolean; totpEnabled: boolean; recoveryCodesLeft: number }

const sectionTitle: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: theme.color.muted, marginBottom: 14,
};
const label: React.CSSProperties = { display: "block", fontSize: 12, fontWeight: 600, color: theme.color.text, marginBottom: 6 };
const hint: React.CSSProperties = { fontSize: 12, color: theme.color.subtle, lineHeight: 1.6 };

export function AccountSecurity({ user, onChanged }: { user: SecurityUser; onChanged: () => void }) {
  if (!user.hasLocalPassword) {
    return (
      <div style={{ ...card, background: "var(--c-panelAlt)", fontSize: 12, color: theme.color.subtle, lineHeight: 1.6 }}>
        <strong style={{ color: theme.color.text }}>Password &amp; MFA</strong><br />
        Authentication is managed by your identity provider (Microsoft Entra ID or Google Workspace).
        To change your password or manage MFA, contact your IT administrator or visit your identity provider's portal.
      </div>
    );
  }
  return (
    <>
      <PasswordCard />
      <AuthenticatorCard user={user} onChanged={onChanged} />
    </>
  );
}

function PasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext]       = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy]       = useState(false);
  const [msg, setMsg]         = useState<{ ok: boolean; text: string } | null>(null);

  const mismatch = confirm.length > 0 && confirm !== next;
  const canSave = !!current && passwordLooksAcceptable(next) && confirm === next && !busy;

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!canSave) return;
    setBusy(true); setMsg(null);
    try {
      // The API revokes every other session and re-issues this one's cookie itself.
      await api.patch<{ refreshable: boolean }>("/auth/password", { currentPassword: current, newPassword: next });
      setCurrent(""); setNext(""); setConfirm("");
      setMsg({ ok: true, text: "Password changed. Every other session has been signed out." });
    } catch (err) {
      setMsg({ ok: false, text: apiErrorMessage(err, "Could not change the password.") });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ ...card, marginBottom: 16 }}>
      <div style={sectionTitle}>Password</div>
      <form onSubmit={save} style={{ display: "grid", gap: 12, maxWidth: 420 }}>
        <div>
          <label style={label} htmlFor="pw-current">Current password</label>
          <input id="pw-current" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} style={inputStyle} />
        </div>
        <div>
          <label style={label} htmlFor="pw-next">New password</label>
          <input id="pw-next" type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} style={inputStyle} />
          <div style={{ marginTop: 6 }}><PasswordRules password={next} /></div>
        </div>
        <div>
          <label style={label} htmlFor="pw-confirm">Confirm new password</label>
          <input id="pw-confirm" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} style={inputStyle} />
          {mismatch && <div style={{ fontSize: 12, color: theme.color.dangerText, marginTop: 4 }}>The passwords do not match.</div>}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button type="submit" disabled={!canSave} style={{ ...btnPrimary, opacity: canSave ? 1 : 0.5 }}>{busy ? "Saving…" : "Change password"}</button>
          {msg && <span style={{ fontSize: 12, color: msg.ok ? theme.color.successText : theme.color.dangerText }}>{msg.text}</span>}
        </div>
      </form>
    </div>
  );
}

type Action = "replace" | "codes";

function AuthenticatorCard({ user, onChanged }: { user: SecurityUser; onChanged: () => void }) {
  const [action, setAction]     = useState<Action | null>(null);
  const [password, setPassword] = useState("");
  const [code, setCode]         = useState("");
  const [busy, setBusy]         = useState(false);
  const [error, setError]       = useState("");
  // Set once the password + code have been accepted for a replacement; the
  // enrolment component re-sends them with its setup call.
  const [proof, setProof]       = useState<{ password: string; code: string } | null>(null);
  const [codes, setCodes]       = useState<string[] | null>(null);
  const [copied, setCopied]     = useState(false);

  function reset() {
    setAction(null); setPassword(""); setCode(""); setError(""); setProof(null); setCodes(null); setCopied(false);
  }

  async function submitProof(e: React.FormEvent) {
    e.preventDefault();
    if (!password || code.replace(/\s/g, "").length !== 6) return;
    setBusy(true); setError("");
    try {
      if (action === "codes") {
        const r = await api.post<{ recoveryCodes: string[] }>("/auth/mfa/recovery-codes", { password, code: code.replace(/\s/g, "") });
        setCodes(r.recoveryCodes);
        onChanged();
      } else {
        // The enrolment panel makes the setup call itself; the proof travels with it.
        setProof({ password, code: code.replace(/\s/g, "") });
      }
    } catch (err) {
      setError(apiErrorMessage(err, "Invalid password or code."));
    } finally {
      setBusy(false);
    }
  }

  function copyCodes() {
    if (!codes) return;
    navigator.clipboard?.writeText(codes.join("\n")).then(() => setCopied(true)).catch(() => {});
  }

  return (
    <div style={{ ...card, marginBottom: 16 }}>
      <div style={sectionTitle}>Authenticator app</div>
      <div style={{ ...hint, marginBottom: 12 }}>
        {user.totpEnabled
          ? <>Two-factor authentication is <strong style={{ color: theme.color.successText }}>on</strong>. You have <strong>{user.recoveryCodesLeft}</strong> unused recovery {user.recoveryCodesLeft === 1 ? "code" : "codes"}.</>
          : <>Two-factor authentication is not set up. You will be asked to set it up at your next sign-in.</>}
      </div>

      {user.totpEnabled && !action && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button type="button" onClick={() => setAction("replace")} style={btnSecondary}>Replace authenticator app</button>
          <button type="button" onClick={() => setAction("codes")} style={btnSecondary}>Get new recovery codes</button>
        </div>
      )}

      {action && !proof && !codes && (
        <form onSubmit={submitProof} style={{ display: "grid", gap: 12, maxWidth: 420 }}>
          <div style={hint}>
            {action === "replace"
              ? "Moving to a new phone? Confirm your password and a code from your current app, then scan the new code with the new phone. Your old authenticator stops working once the new one is confirmed."
              : "Confirm your password and a code from your app. Your existing recovery codes stop working as soon as the new ones are issued."}
          </div>
          <div>
            <label style={label} htmlFor="mfa-password">Password</label>
            <input id="mfa-password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={label} htmlFor="mfa-code">Code from your authenticator app</label>
            <input id="mfa-code" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value.replace(/[^\d\s]/g, "").slice(0, 7))} style={{ ...inputStyle, width: 160, letterSpacing: "0.12em", textAlign: "center" }} placeholder="123 456" />
          </div>
          {error && <div style={{ fontSize: 12, color: theme.color.dangerText }}>{error}</div>}
          <div style={{ display: "flex", gap: 8 }}>
            <button type="submit" disabled={busy || !password || code.replace(/\s/g, "").length !== 6} style={{ ...btnPrimary, opacity: busy || !password || code.replace(/\s/g, "").length !== 6 ? 0.5 : 1 }}>
              {busy ? "Checking…" : "Continue"}
            </button>
            <button type="button" onClick={reset} style={btnSecondary}>Cancel</button>
          </div>
        </form>
      )}

      {action === "replace" && proof && (
        <TotpEnrolment
          proof={proof}
          onCancel={reset}
          onDone={({ accessToken }) => {
            setLocalSession(accessToken, true);
            reset();
            onChanged();
          }}
        />
      )}

      {action === "codes" && codes && (
        <div>
          <div style={{ ...hint, marginBottom: 12 }}>
            Your new recovery codes. Each signs you in once if you lose your phone; this is the only time they are shown.
          </div>
          <div style={{
            display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 18px", padding: 14, borderRadius: theme.radius.md, maxWidth: 420,
            background: theme.color.panelAlt, border: `1px solid ${theme.color.border}`, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize: 14, color: theme.color.heading, letterSpacing: "0.04em", marginBottom: 12,
          }}>
            {codes.map((c) => <div key={c}>{c}</div>)}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" onClick={copyCodes} style={btnSecondary}>{copied ? "Copied" : "Copy"}</button>
            <button type="button" onClick={reset} style={btnPrimary}>I have saved them</button>
          </div>
        </div>
      )}
    </div>
  );
}
